import React, { useRef, useEffect } from 'react';
import { Layer, Shape } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { useMap } from '../../contexts/MapContext';

// ── Noise texture ──────────────────────────────────────────────────────────

function hash(x, y) {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function smoothNoise(x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix,        fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash(ix,   iy),   b = hash(ix+1, iy);
  const c = hash(ix,   iy+1), d = hash(ix+1, iy+1);
  return a + (b-a)*ux + (c-a)*uy + (d-b-c+a)*ux*uy;
}

function fbm(x, y) {
  return (
    smoothNoise(x * 1.0, y * 1.0) * 0.50 +
    smoothNoise(x * 2.1, y * 2.1) * 0.25 +
    smoothNoise(x * 4.3, y * 4.3) * 0.15 +
    smoothNoise(x * 8.7, y * 8.7) * 0.10
  );
}

function createNoiseTexture() {
  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width  = SIZE;
  canvas.height = SIZE;
  const ctx  = canvas.getContext('2d');
  const img  = ctx.createImageData(SIZE, SIZE);
  const data = img.data;

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const v   = fbm((x / SIZE) * 5, (y / SIZE) * 5);
      const idx = (y * SIZE + x) * 4;
      const base = Math.floor(v * 33) + 5;
      data[idx]     = base;
      data[idx + 1] = base;
      data[idx + 2] = base + 10;
      data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function createFogTexture() {
  const SIZE = 512;
  const canvas = document.createElement('canvas');
  canvas.width  = SIZE;
  canvas.height = SIZE;
  const ctx  = canvas.getContext('2d');
  const img  = ctx.createImageData(SIZE, SIZE);
  const data = img.data;

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const v0 = fbm((x / SIZE) * 2.5, (y / SIZE) * 2.5);
      const v1 = fbm((x / SIZE) * 5.0 + 3.1, (y / SIZE) * 5.0 + 1.7);
      const v2 = fbm((x / SIZE) * 10.0 + 7.3, (y / SIZE) * 10.0 + 5.9);
      const v  = v0 * 0.55 + v1 * 0.30 + v2 * 0.15;

      const idx = (y * SIZE + x) * 4;
      data[idx]     = Math.floor(6  + v * 22);
      data[idx + 1] = Math.floor(4  + v * 14);
      data[idx + 2] = Math.floor(18 + v * 55);
      data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

let _noiseTexture = null;
function getNoiseTexture() {
  if (!_noiseTexture && typeof document !== 'undefined') _noiseTexture = createNoiseTexture();
  return _noiseTexture;
}

let _fogTexture = null;
function getFogTexture() {
  if (!_fogTexture && typeof document !== 'undefined') _fogTexture = createFogTexture();
  return _fogTexture;
}

const EDGE_DEPTH = CELL_SIZE * 1.1;

const NEIGHBORS = [
  { dx: -1, dy:  0, side: 'left'   },
  { dx:  1, dy:  0, side: 'right'  },
  { dx:  0, dy: -1, side: 'top'    },
  { dx:  0, dy:  1, side: 'bottom' },
];

// Draw an edge shadow on a revealed cell face pointing into unrevealed fog.
function drawEdgeShadow(ctx, x, y, side, shadowDark) {
  const wx = x * CELL_SIZE, wy = y * CELL_SIZE;
  let gx0, gy0, gx1, gy1;
  if (side === 'left')   { gx0 = wx + CELL_SIZE; gy0 = wy; gx1 = wx + CELL_SIZE - EDGE_DEPTH; gy1 = wy; }
  else if (side === 'right')  { gx0 = wx; gy0 = wy; gx1 = wx + EDGE_DEPTH; gy1 = wy; }
  else if (side === 'top')    { gx0 = wx; gy0 = wy + CELL_SIZE; gx1 = wx; gy1 = wy + CELL_SIZE - EDGE_DEPTH; }
  else                        { gx0 = wx; gy0 = wy; gx1 = wx; gy1 = wy + EDGE_DEPTH; }
  const grad = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
  grad.addColorStop(0, shadowDark);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(wx, wy, CELL_SIZE, CELL_SIZE);
}

// ── Component ──────────────────────────────────────────────────────────────

export default function FogLayer({ isDm = false, gridW, gridH }) {
  const { fog }    = useMap();
  const texture    = getNoiseTexture();
  const fogTexture = getFogTexture();

  // Keep a shape ref so we can ensure redraw when `fog` changes. Konva's
  // Shape doesn't know its sceneFunc result changed just because a ref did.
  const dmShapeRef     = useRef(null);
  const playerShapeRef = useRef(null);
  const edgeShapeRef   = useRef(null);

  useEffect(() => {
    dmShapeRef.current?.getLayer()?.batchDraw();
    playerShapeRef.current?.getLayer()?.batchDraw();
    edgeShapeRef.current?.getLayer()?.batchDraw();
  }, [fog, gridW, gridH, isDm]);

  if (!gridW || !gridH) return null;

  const totalW = gridW * CELL_SIZE;
  const totalH = gridH * CELL_SIZE;

  // Shared edge-shadow drawer
  const sceneDrawEdges = (ctx) => {
    const shadowDark = isDm ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.78)';
    for (let y = 0; y < gridH; y++) {
      for (let x = 0; x < gridW; x++) {
        if (!fog[y * gridW + x]) continue;
        for (const { dx, dy, side } of NEIGHBORS) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || nx >= gridW || ny < 0 || ny >= gridH) continue;
          if (fog[ny * gridW + nx]) continue;
          drawEdgeShadow(ctx, x, y, side, shadowDark);
        }
      }
    }
  };

  if (isDm) {
    // DM view: textured semi-transparent fog over unrevealed cells
    const sceneDm = (ctx, shape) => {
      if (!texture) return;
      const pattern = ctx.createPattern(texture, 'repeat');
      ctx.globalAlpha = 0.62;
      ctx.fillStyle = pattern;
      for (let y = 0; y < gridH; y++) {
        for (let x = 0; x < gridW; x++) {
          if (fog[y * gridW + x]) continue;
          ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }
      }
      ctx.globalAlpha = 1;
      ctx.fillStrokeShape(shape);
    };
    return (
      <Layer listening={false}>
        <Shape ref={dmShapeRef} sceneFunc={sceneDm} perfectDrawEnabled={false} listening={false}
               x={0} y={0} width={totalW} height={totalH} />
        <Shape ref={edgeShapeRef} sceneFunc={(ctx, shape) => { sceneDrawEdges(ctx); ctx.fillStrokeShape(shape); }}
               perfectDrawEnabled={false} listening={false}
               x={0} y={0} width={totalW} height={totalH} />
      </Layer>
    );
  }

  // Player view: one full fog rect with punched holes for revealed cells
  const scenePlayer = (ctx, shape) => {
    if (fogTexture) {
      const pattern = ctx.createPattern(fogTexture, 'repeat');
      ctx.fillStyle = pattern;
    } else {
      ctx.fillStyle = '#050512';
    }
    ctx.fillRect(0, 0, totalW, totalH);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    for (let y = 0; y < gridH; y++) {
      for (let x = 0; x < gridW; x++) {
        if (!fog[y * gridW + x]) continue;
        ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
      }
    }
    ctx.restore();
    ctx.fillStrokeShape(shape);
  };

  // Player view: fog rect + edge shadows share a single Layer (was 2 layers).
  // Both Shapes live in the same canvas — fewer iOS composition costs.
  return (
    <Layer listening={false}>
      <Shape ref={playerShapeRef} sceneFunc={scenePlayer} perfectDrawEnabled={false} listening={false}
             x={0} y={0} width={totalW} height={totalH} />
      <Shape ref={edgeShapeRef} sceneFunc={(ctx, shape) => { sceneDrawEdges(ctx); ctx.fillStrokeShape(shape); }}
             perfectDrawEnabled={false} listening={false}
             x={0} y={0} width={totalW} height={totalH} />
    </Layer>
  );
}
